/**
 * Cribbage — server-authoritative rules engine (two-player, six-card game, race to 121).
 *
 * Rules implemented (toggles live in {@link CribbageConfig}):
 *  - Six cards each; both discard two to the dealer's crib (the non-dealer first), then the starter is cut. A jack
 *    cut as starter scores the dealer 2 ("his heels").
 *  - Pegging: starting with the non-dealer, players alternate playing a card, adding its value to a running count that
 *    may not pass 31. Points for a count of fifteen or thirty-one (2 each), pairs (2/6/12) and runs of three or more
 *    (any order). A player who cannot play says "go" (automatic); the other continues, and the last card played scores
 *    1 (unless it made 31). The count then restarts, led by the player who did not play last.
 *  - The show: the non-dealer counts their hand, then the dealer their hand, then the crib (fifteens, pairs, runs,
 *    flush, nobs). Points are pegged as soon as they are earned: the first to reach `targetScore` wins on the spot.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';
import { cribbagePegScore, cribbageScoreHand, cribbageValue, type CribbageScoreItem } from './score.js';

export interface CribbageConfig {
  /** First player to peg this many points wins. */
  targetScore: number;
  /** Hard cap on deals so a match always terminates. */
  maxRounds: number;
  cutDelayMs: number;
  showDelayMs: number;
  roundEndDelayMs: number;
}

export type CribbagePhase = 'DISCARD' | 'CUT' | 'PEGGING' | 'SHOW' | 'ROUND_END' | 'MATCH_END';

export interface CribbageShow {
  who: 'pone' | 'dealer' | 'crib';
  seat: number;
  cards: CardId[];
  points: number;
  items: CribbageScoreItem[];
}

export interface CribbagePegEvent {
  seat: number;
  points: number;
  reasons: string[];
}

export interface CribbageState {
  config: CribbageConfig;
  players: string[];
  round: number;
  dealer: number;
  phase: CribbagePhase;
  hands: CardId[][];
  /** The four cards each player kept (scored in the show). */
  kept: CardId[][];
  crib: CardId[];
  starter: CardId | null;
  deck: CardId[];
  turn: number;
  /** Cards in the current count, in play order. */
  pile: CardId[];
  count: number;
  lastPlayer: number | null;
  /** Everything each seat has played this deal, in order. */
  played: CardId[][];
  showStage: number;
  shown: CribbageShow[];
  lastPeg: CribbagePegEvent | null;
  scores: number[];
  /** Points each player has earned so far this deal. */
  roundGains: number[];
  /** Points each player earned per finished deal, pegging and counting together. */
  roundResults: { round: number; dealer: number; gains: number[] }[];
}

export type CribbageAction = { type: 'discard'; cards: [CardId, CardId] } | { type: 'play'; card: CardId };

export type CribbageLegal = { kind: 'discard'; count: 2 } | { kind: 'play'; cards: CardId[] } | { kind: 'none' };

export interface CribbageView {
  gameType: 'CRIBBAGE';
  config: CribbageConfig;
  seat: number | null;
  phase: CribbagePhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  cribCount: number;
  starter: CardId | null;
  pile: CardId[];
  count: number;
  played: CardId[][];
  showStage: number;
  shown: CribbageShow[];
  lastPeg: CribbagePegEvent | null;
  scores: number[];
  roundResults: { round: number; dealer: number; gains: number[] }[];
  totals: number[];
}

const DEFAULTS: CribbageConfig = {
  targetScore: 121,
  maxRounds: 30,
  cutDelayMs: 900,
  showDelayMs: 2800,
  roundEndDelayMs: 3500,
};

const SEATS = 2;

/** Cards a seat may play on the current count. */
export function cribbagePlayable(hand: readonly CardId[], count: number): CardId[] {
  return hand.filter((c) => count + cribbageValue(c) <= 31);
}

/** Best two cards to throw away judged on the four kept alone (no starter, no crib): the engine's timeout pick. */
export function cribbageSimpleDiscard(hand: readonly CardId[]): [CardId, CardId] {
  let best: [CardId, CardId] = [hand[0]!, hand[1]!];
  let bestScore = -1;
  for (let i = 0; i < hand.length; i++) {
    for (let j = i + 1; j < hand.length; j++) {
      const keep = hand.filter((_, k) => k !== i && k !== j);
      const score = cribbageScoreHand(keep, null, false).points;
      if (score > bestScore) {
        bestScore = score;
        best = [hand[i]!, hand[j]!];
      }
    }
  }
  return best;
}

function deal(state: CribbageState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], []];
  for (let i = 0; i < 6; i++) for (let s = 0; s < SEATS; s++) hands[s]!.push(deck.pop()!);
  state.hands = hands.map((h) => sortHand(h));
  state.kept = [[], []];
  state.crib = [];
  state.starter = null;
  state.deck = deck;
  state.pile = [];
  state.count = 0;
  state.lastPlayer = null;
  state.played = [[], []];
  state.showStage = 0;
  state.shown = [];
  state.lastPeg = null;
  state.phase = 'DISCARD';
  state.turn = 1 - state.dealer;
  events.push({ type: 'dealt', round: state.round, dealer: state.dealer, counts: [6, 6] });
}

/** Adds points for a seat and ends the match on reaching the target. Returns true when the match is over. */
function peg(state: CribbageState, seat: number, points: number, reasons: string[], events: EngineEvent[]): boolean {
  if (points <= 0) return false;
  state.scores[seat]! += points;
  state.lastPeg = { seat, points, reasons };
  state.roundGains[seat]! += points;
  events.push({ type: 'peg', seat, points, reasons, scores: [...state.scores] });
  if (state.scores[seat]! >= state.config.targetScore) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standingsFromScores(state.scores.map((score, s) => ({ seat: s, score }))) });
    return true;
  }
  return false;
}

/** Restarts the count; `lastPlayer` scores nothing further. Returns false when no cards remain to be played. */
function nextLead(state: CribbageState, last: number): boolean {
  state.pile = [];
  state.count = 0;
  const other = 1 - last;
  if (state.hands[other]!.length > 0) state.turn = other;
  else if (state.hands[last]!.length > 0) state.turn = last;
  else return false;
  return true;
}

function startShow(state: CribbageState): void {
  state.phase = 'SHOW';
  state.showStage = 0;
}

export const cribbageEngine: GameEngine<CribbageConfig, CribbageState, CribbageAction, CribbageView, CribbageLegal> = {
  gameType: 'CRIBBAGE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 21, 241);
    checkInt('maxRounds', c.maxRounds, 1, 60);
    checkInt('cutDelayMs', c.cutDelayMs, 0, 10000);
    checkInt('showDelayMs', c.showDelayMs, 0, 30000);
    checkInt('roundEndDelayMs', c.roundEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Cribbage needs exactly 2 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: CribbageState = {
      config,
      players,
      round: 1,
      dealer: rng.int(SEATS),
      phase: 'DISCARD',
      hands: [],
      kept: [],
      crib: [],
      starter: null,
      deck: [],
      turn: 0,
      pile: [],
      count: 0,
      lastPlayer: null,
      played: [],
      showStage: 0,
      shown: [],
      lastPeg: null,
      scores: [0, 0],
      roundResults: [],
      roundGains: [0, 0],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    if (raw.type === 'discard' && Array.isArray(raw.cards) && raw.cards.length === 2 && raw.cards.every((c) => typeof c === 'string' && c.length <= 4)) {
      return { type: 'discard', cards: [raw.cards[0] as string, raw.cards[1] as string] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Cribbage action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine((state.phase === 'DISCARD' || state.phase === 'PEGGING') && seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const hand = state.hands[seat]!;

    if (action.type === 'discard') {
      assertEngine(state.phase === 'DISCARD', 'INVALID_PHASE', 'Not discarding now');
      const [a, b] = action.cards;
      assertEngine(a !== b && hand.includes(a) && hand.includes(b), 'INVALID_CARD', 'Choose two different cards from your hand');
      removeCards(hand, [a, b]);
      state.crib.push(a, b);
      state.kept[seat] = [...hand];
      events.push({ type: 'discarded', seat });
      if (state.kept[0]!.length === 4 && state.kept[1]!.length === 4) {
        state.phase = 'CUT';
      } else {
        state.turn = 1 - seat;
      }
      return events;
    }

    assertEngine(state.phase === 'PEGGING', 'INVALID_PHASE', 'Not pegging now');
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(cribbagePlayable(hand, state.count).includes(action.card), 'ILLEGAL_MOVE', 'That card would take the count past 31');
    removeCards(hand, [action.card]);
    state.pile.push(action.card);
    state.played[seat]!.push(action.card);
    state.count += cribbageValue(action.card);
    state.lastPlayer = seat;
    events.push({ type: 'cardPlayed', seat, card: action.card, count: state.count });
    const scored = cribbagePegScore(state.pile, state.count);
    if (peg(state, seat, scored.points, scored.reasons, events)) return events;

    const other = 1 - seat;
    if (state.count === 31) {
      if (!nextLead(state, seat)) startShow(state);
      return events;
    }
    if (cribbagePlayable(state.hands[other]!, state.count).length > 0) {
      state.turn = other;
    } else if (cribbagePlayable(hand, state.count).length > 0) {
      events.push({ type: 'go', seat: other });
      state.turn = seat;
    } else {
      // Neither can play: the last card scores one and the count restarts.
      events.push({ type: 'go', seat: other });
      if (peg(state, seat, 1, ['lastCard'], events)) return events;
      if (!nextLead(state, seat)) startShow(state);
    }
    return events;
  },

  getActors(state) {
    return state.phase === 'DISCARD' || state.phase === 'PEGGING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'DISCARD') return { kind: 'discard', count: 2 };
    if (state.phase === 'PEGGING') return { kind: 'play', cards: cribbagePlayable(state.hands[seat]!, state.count) };
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'DISCARD') return { type: 'discard', cards: cribbageSimpleDiscard(state.hands[seat]!) };
    const playable = cribbagePlayable(state.hands[seat]!, state.count);
    return { type: 'play', card: [...playable].sort((a, b) => cribbageValue(a) - cribbageValue(b))[0]! };
  },

  getView(state, seat) {
    return {
      gameType: 'CRIBBAGE',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.round,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      cribCount: state.crib.length,
      starter: state.starter,
      pile: [...state.pile],
      count: state.count,
      played: state.played.map((p) => [...p]),
      showStage: state.showStage,
      shown: state.shown.map((x) => ({ ...x, cards: [...x.cards], items: x.items.map((i) => ({ ...i, cards: [...i.cards] })) })),
      lastPeg: state.lastPeg ? { ...state.lastPeg, reasons: [...state.lastPeg.reasons] } : null,
      scores: [...state.scores],
      roundResults: state.roundResults.map((r) => ({ ...r, gains: [...r.gains] })),
      totals: [...state.scores],
    };
  },

  getSystemStep(state) {
    if (state.phase === 'CUT') return { kind: 'cut', delayMs: state.config.cutDelayMs };
    if (state.phase === 'SHOW') return { kind: 'show', delayMs: state.config.showDelayMs };
    if (state.phase === 'ROUND_END') return { kind: 'nextRound', delayMs: state.config.roundEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'CUT') {
      state.starter = state.deck[rng.int(state.deck.length)]!;
      events.push({ type: 'starter', card: state.starter });
      state.phase = 'PEGGING';
      state.turn = 1 - state.dealer;
      if (parseCard(state.starter).rank === 11 && peg(state, state.dealer, 2, ['heels'], events)) return events;
      return events;
    }
    if (state.phase === 'SHOW') {
      const pone = 1 - state.dealer;
      const stage = state.showStage;
      const who = stage === 0 ? 'pone' : stage === 1 ? 'dealer' : 'crib';
      const seat = stage === 0 ? pone : state.dealer;
      const cards = stage === 2 ? [...state.crib] : [...state.kept[seat]!];
      const result = cribbageScoreHand(cards, state.starter, stage === 2);
      state.shown.push({ who, seat, cards: sortHand(cards), points: result.points, items: result.items });
      state.showStage++;
      events.push({ type: 'show', who, seat, points: result.points });
      if (peg(state, seat, result.points, [who], events)) return events;
      if (state.showStage >= 3) {
        state.roundResults.push({ round: state.round, dealer: state.dealer, gains: [...state.roundGains] });
        if (state.round >= state.config.maxRounds) {
          state.phase = 'MATCH_END';
          events.push({ type: 'matchEnd', standings: standingsFromScores(state.scores.map((score, s) => ({ seat: s, score }))) });
        } else {
          state.phase = 'ROUND_END';
        }
      }
      return events;
    }
    if (state.phase === 'ROUND_END') {
      state.round++;
      state.dealer = 1 - state.dealer;
      state.roundGains = [0, 0];
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standingsFromScores(state.scores.map((score, seat) => ({ seat, score })));
  },

  getRoundsCompleted: (state) => state.roundResults.length,
};
